
import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Select,
  MenuItem,
  Tooltip,
  ButtonGroup
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { SiteConfig, sites } from '../siteConfig';
import { SupportAgent } from '@mui/icons-material';
import { Phone, PhoneIcon } from 'lucide-react';

import { useTranslation } from "react-i18next";
import LanguageIcon from "@mui/icons-material/Language";

export default function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Determine current site key
  const path = location.pathname.split('/')[1] as keyof typeof sites;
  const siteConfig: SiteConfig = sites[path] || sites.itsupport; // default site
  const PHONE_NUMBER = sites[path]?.phonenumber || sites.itsupport.phonenumber;
  const isWorkflowSite = path === 'workflow';

  // Get the header title based on site
  const getSiteTitle = () => {
    switch (path) {

      case 'guidance':
        return t("header:guidanceTitle", { defaultValue: siteConfig.name });
      case 'itsupport':
        return t("header:itsupportTitle", { defaultValue: siteConfig.name });
      case 'workflow':
        return t("workflowheader:siteTitle", { defaultValue: siteConfig.name });
      default:
        return siteConfig.name;
    }
  };
  // Apply dynamic CSS variables to body
  useEffect(() => {
    document.body.style.setProperty('--primary', siteConfig.primaryColor);
    document.body.style.setProperty('--header-bg', siteConfig.headerBgShade || siteConfig.primaryColor);
    document.body.style.setProperty('--header-text', siteConfig.headerTextColor || '#fff');
    document.body.style.paddingTop = `${siteConfig.headerHeight || 64}px`;
  }, [siteConfig]);

  return (
    <>
      <AppBar
        sx={{

          backgroundColor: 'var(--header-bg)',
          color: 'var(--header-text)',
          height: siteConfig.headerHeight || 64,
          boxShadow: 'none',
          borderBottom: '1px solid #e2e8f0',
          transition: 'all 0.3s ease',
          '& *': {
            fontSize: { xs: 12, md: 24 }
          }
        }}
      >
        <Toolbar sx={{
          minHeight: '100% !important',
          justifyContent: 'space-between',
          px: { xs: 1, sm: 2, md: 3 }
        }}>
          {/* Left Section: Logo and Site Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link
              to={isWorkflowSite ? `/${path}/home` : "/home"}
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '16px' }}
            >
              <img
                src={siteConfig.logo}
                alt="Logo"
                style={{
                  width: siteConfig.logoWidth || 80,
                  height: siteConfig.logoHeight || 80,
                  objectFit: 'contain',
                  borderRadius: '4px'
                }}
              />
            </Link>
            <Typography
              variant="h6"
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontWeight: "bold",
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.2rem' },
                color: 'var(--header-text)',
              }}
            >

              {t(getSiteTitle())}
              {/* {t("logoTitle", { siteConfig: siteConfig.name })} */}
            </Typography>
          </Box>
          <Box sx={{ alignItems: "center" }} >
            <Typography
              variant="h6"
              sx={{
                display: { xs: 'inline-flex', sm: 'block', md: 'block' },
                fontWeight: "bold",
                fontSize: { xs: '0.9rem', sm: '1.25rem', md: '1.7rem' },
                color: '#1e40af',
              }}
            >
              {siteConfig.companyname}

            </Typography>
          </Box>

          <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'flex' }, alignItems: 'center', gap: 2, mr: { xs: 1, sm: 2, md: 6 } }}>
            <Link
              to={isWorkflowSite ? `/${path}/contact` : "/contact"}

              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '16px' }}
            >

              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                  color: 'var(--header-text)',

                }}
              >
                <Button
                  startIcon={<PhoneIcon />}
                  variant="outlined"
                  sx={{ color: 'var(--header-text)', borderColor: 'transparent' }}
                >
                  {PHONE_NUMBER}
                </Button>
                {' '} {t("contact")}
              </Typography>
            </Link>

          </Box>
          {/* Language Switcher */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LanguageIcon sx={{ display: { xs: 'none', sm: 'flex', md: 'flex' }, color: "var(--header-text)", fontSize: 20 }} />
            <ButtonGroup variant="outlined" size="small" sx={{ borderColor: "rgba(255, 255, 255, 0.3)" }}>
              <Tooltip title="Dansk">
                <Button
                  onClick={() => i18n.changeLanguage("da")}
                  sx={{
                    color: i18n.language === "da" ? "white" : "rgba(255, 255, 255, 0.8)",
                    backgroundColor: i18n.language === "da" ? "rgba(255, 255, 255, 0.2)" : "transparent",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    minWidth: 60,
                    fontWeight: i18n.language === "da" ? "bold" : "normal",
                    "&:hover": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  DA
                </Button>
              </Tooltip>
              <Tooltip title="English">
                <Button
                  onClick={() => i18n.changeLanguage("en")}
                  sx={{
                    color: i18n.language === "en" ? "white" : "rgba(255, 255, 255, 0.8)",
                    backgroundColor: i18n.language === "en" ? "rgba(255, 255, 255, 0.2)" : "transparent",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    minWidth: 60,
                    fontWeight: i18n.language === "en" ? "bold" : "normal",
                    "&:hover": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  EN
                </Button>
              </Tooltip>
              <Tooltip title="Svenska">
                <Button
                  onClick={() => i18n.changeLanguage("sv")}
                  sx={{
                    color: i18n.language === "sv" ? "white" : "rgba(255, 255, 255, 0.8)",
                    backgroundColor: i18n.language === "sv" ? "rgba(255, 255, 255, 0.2)" : "transparent",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    minWidth: 60,
                    fontWeight: i18n.language === "sv" ? "bold" : "normal",
                    "&:hover": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  SV
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
}
